import PropTypes from "prop-types";
import moment from "moment";
import orderService from "../../../Seating/Security/OrderService/orderService";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";
import { closeSnackbar as NotistackCloseSnackbar } from "notistack";
import CardClass from "../../CardClass";
import DataGrid from "../../DataGrid";
import Progress from "../../Progress";
import Setting from "../../../Utility/Setting";
import User from "../../../Utility/Crud/User";
import OrderStatusReason from "../../../Utility/Crud/OrderStatusReason";
import OrderActivityType from "../../../Utility/Crud/OrderActivityType";
import InsuranceType from "../../../Utility/Crud/InsuranceType";
import InsuranceSubType from "../../../Utility/Crud/InsuranceSubType";
import Location from "../../../Utility/Crud/Location";
import Region from "../../../Utility/Crud/Region";
import ProductType from "../../../Utility/Crud/ProductType";
import ProductSubType from "../../../Utility/Crud/ProductSubType";
import OrderStatus from "../../../Utility/Crud/OrderStatus";
import IssueCategory from "../../../Utility/Crud/IssueCategory";
import IssueReason from "../../../Utility/Crud/IssueReason";
import Term from "../../../Utility/Crud/Term";
import DialogQueueHelp from "../../Dialog/DialogQueueHelp";
import Queue from "../../../Utility/Crud/Queue";
import Vendor from "../../../Utility/Crud/Vendor";
import Security from "../../../Utility/Security";

import MUICard from "@mui/material/Card";
import MUIRefreshIcon from '@mui/icons-material/Refresh';
import MUIFilterAltIcon from '@mui/icons-material/FilterAlt';
import MUIVisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MUIFileDownloadIcon from '@mui/icons-material/FileDownload';
import MUIListItemIcon from '@mui/material/ListItemIcon';
import MUIMenuItem from "@mui/material/MenuItem";
import MUICloudSyncIcon from "@mui/icons-material/CloudSync";
import MUIOpenInNewIcon from '@mui/icons-material/OpenInNew';
import MUIHelpIcon from '@mui/icons-material/Help';
import MUIWarningIcon from '@mui/icons-material/Warning';
import MUIStack from "@mui/material/Stack";
import MUITypography from "@mui/material/Typography";
import MUIBox from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import Exporter from "../../../Utility/Exporter";
import MarketingCampaignCategory from "../../../Utility/Crud/MarketingCampaignCategory";
import MarketingCampaign from "../../../Utility/Crud/MarketingCampaign";
import ServicePartCatalog from "../../../Utility/Crud/ServicePartCatalog";
import DistributionReason from "../../../Utility/Crud/DistributionReason";
import MainContext from "../../../Context/MainContext/MainContext";
import OrderStatusReasonDetail from "../../../Utility/Crud/OrderStatusReasonDetail";
import Calendar from "../../../Utility/Calendar";

export default class CardQueueDataGrid extends CardClass {
    static contextType = MainContext; // For legacy class components

    constructor(props) {
        super(props);

        this.state.viewOutOfBoundsOnly = this.props.viewOutOfBoundsOnly || false;
        this.state.formattedRows = [];
    }

    /**
     * Handle removes or adds to the rows prop.
     */
    componentDidUpdate(prevProps) {
        if (prevProps.loadedAt !== this.props.loadedAt) {
            this.setState({
                formattedRows: this.getFormattedRows(this.state.viewOutOfBoundsOnly)
            });
        }
    }

    /**
     * Pull settings and save them to state.
     */
    componentDidMount() {
        // Get active grid column names (only columns currently in the queue view)
        const queueColumnNames = this.props.queue.view.columns.filter((column) => this.columns[column]);

        // Get saved settings
        const savedSorting = Setting.get(`queue.${this.props.queue.id}.sorting`);
        const savedGrouping = Setting.get(`queue.${this.props.queue.id}.grouping`);

        // Check if saved sorting settings have valid column names
        const hasSavedSortingValidColumns = savedSorting && savedSorting.every(sort =>
            queueColumnNames.includes(sort.columnName)
        );

        if (savedSorting !== undefined && hasSavedSortingValidColumns) {
            // Use the saved sorting. Filter out columns that no longer exist
            // and if there are any save that as the new setting.
            this.state.sorting = savedSorting.filter(sorting => queueColumnNames.includes(sorting.columnName));
            if (this.state.sorting.length !== savedSorting.length) {
                Setting.set(`queue.${this.props.queue.id}.sorting`, this.state.sorting);
            }
        } else {
            // Convert the queue sorting object to one that the grid understands.
            this.state.sorting = this.props.queue.view.sorts ? this.props.queue.view.sorts.map((sort) => ({
                columnName: sort.key,
                direction: sort.direction,
            })).filter(sort => queueColumnNames.includes(sort.columnName)) : [];
        }

        // Check if saved grouping settings have valid column names
        const hasSavedGroupingValidColumns = savedGrouping && savedGrouping.every(sort =>
            queueColumnNames.includes(sort.columnName)
        );

        if (savedGrouping !== undefined && hasSavedGroupingValidColumns) {
            // Use the saved grouping. Filter out columns that no longer exist
            // and if there are any save that as the new setting.
            this.state.grouping = savedGrouping.filter(grouping => queueColumnNames.includes(grouping.columnName));
            if (this.state.grouping.length !== savedGrouping.length) {
                Setting.set(`queue.${this.props.queue.id}.grouping`, this.state.grouping);
            }
        } else {
            // Convert the queue grouping object to one that the grid understands.
            this.state.grouping = this.props.queue.view.groups ? this.props.queue.view.groups.map((group) => ({
                columnName: group,
            })).filter(group => queueColumnNames.includes(group.columnName)) : [];
        }

        // Ensure default grouping columns are included in the sorting list with ascending order
        const finalSorting = this.updateSortingFromGrouping(this.state.grouping, this.state.sorting, this.props.queue.view.sorts || []);

        this.applyGroupingToSorting(this.state.grouping, finalSorting);

        const savedExpandedGroups = Setting.get(`queue.${this.props.queue.id}.expandedGroups`);
        if (savedExpandedGroups !== undefined) {
            // Use the saved expandedGroups. This does NOT break if keys exist
            // that do not exist in the data.
            this.state.expandedGroups = savedExpandedGroups;
        } else {
            this.state.expandedGroups = [];
        }
    }

    /**
     * @returns A MUI Card component with a header and content. Overrides the
     * default card properties for a custom 100% height look.
     */
    render() {
        return (
            <>
                <MUICard sx={{ position: "relative", height: "100%", background: this.getBackground() }}>
                    <DialogQueueHelp open={this.state.openDialogInfo} header={"Help"} onClose={() => this.setState({ openDialogInfo: false })} />
                    {this.renderContent()}
                    {this.renderInternal()}
                </MUICard>
            </>
        );
    }

    /**
     * Override this in subclasses and put any special render code you need in
     * this function.
     */
    renderInternal() {
        return null;
    }

    /**
    * Renders the Queue Data Grid with custom action menu items.
    */
    renderContent() {
        if (this.props.loadedAt) {
            const columns = [];
            const columnWidths = [];

            this.props.queue.view.columns.forEach((column) => {
                if (this.columns[column]) {
                    columns.push({
                        title: this.columns[column].name,
                        name: column,
                        type: this.getDataGridColumnType(this.columns[column].type),
                        align: this.columns[column].align,
                    });

                    columnWidths.push({
                        columnName: column,
                        width: this.columns[column].width,
                    });
                } else {
                    console.error(`Column not defined: ${column}`);
                }
            });

            const rowActions = [];
            if (this.props.queue.view.rowActions && this.props.queue.view.rowActions.length > 0) {
                this.props.queue.view.rowActions.forEach((rowAction) => {
                    switch (rowAction) {
                        case "syncToBrightree":
                            if (Security.hasPermission("order.syncToBrightree")) {
                                rowActions.push(
                                    {
                                        rowAction: rowAction,
                                        component: (
                                            <MUIMenuItem>
                                                <MUIListItemIcon>
                                                    <MUICloudSyncIcon fontSize="small" />
                                                </MUIListItemIcon>
                                                Sync to Brightree
                                            </MUIMenuItem>
                                        ),
                                        isDisabled: (row) => {
                                            return row["order_activity.brightree_external_id"] !== null;
                                        }
                                    }
                                );
                            }
                            break;
                        default:
                            console.warn("Undefined row action: " + rowAction);
                            break;
                    }
                });

                if (rowActions.length > 0) {
                    columns.unshift({
                        title: " ",
                        name: "rowActions",
                        type: "rowActions"
                    });
                    columnWidths.unshift({
                        columnName: "rowActions",
                        width: 70,
                    });
                }
            }

            if (this.props.loadFailed === true) {
                return (<MUIStack spacing={2} height="100%" display="flex" alignItems="center" justifyContent="center">
                    <MUIBox sx={{ "textAlign": "center" }}>
                        <MUIWarningIcon fontSize="large" color="warning" />
                        <MUITypography display="block" variant="body1" color="textPrimary">
                            Oops; something went wrong.
                        </MUITypography>
                        <MUIButton startIcon={<MUIRefreshIcon />} onClick={this.props.onReload}>TRY AGAIN</MUIButton>
                    </MUIBox>
                </MUIStack>);
            } else {
                return (<DataGrid
                    id={this.props.queue.id}
                    rows={this.state.formattedRows}
                    columns={columns}
                    actions={this.getActions()}
                    rowActions={rowActions}
                    onClick={this.handleClickRow.bind(this)}
                    onClickRowAction={this.handleClickRowAction.bind(this)}
                    sorting={this.state.sorting}
                    onSortingChange={this.handleSortingChange.bind(this)}
                    grouping={this.state.grouping}
                    onGroupingChange={this.handleGroupingChange.bind(this)}
                    expandedGroups={this.state.expandedGroups}
                    onExpandedGroupsChange={this.handleExpandedGroupsChange.bind(this)}
                    defaultColumnWidths={columnWidths}
                    query={this.props.query}
                    compactTopRightSearchbarAndMenuLayout={true}
                    enableGroupingPanel={true}
                    groupSummaryItems={this.getGroupSummaryItems()}
                    showGroupSortingControls={true}
                />);
            }
        }

        return (<Progress message="Loading Queue..." />);
    }

    /**
     * Maps internal formatter types to DataGrid renderer types.
     *
     * @param {string} type Internal column type.
     * @returns {string} DataGrid-compatible type.
     */
    getDataGridColumnType(type) {
        switch (type) {
            case "serviceDocumentationFollowupReceivedNotification":
            case "servicePreauthFollowupReceivedNotification":
                return "booleanCheck";
            case "inboundCommunicationBody":
            case "distributionReasonDescriptions":
                return "stringTooltip";
            default:
                return type;
        }
    }

    /**
     * @returns {array} Actions for the current queue.
     */
    getActions() {
        let actions = [];

        actions.push(
            <MUIMenuItem value={"Reload Queue"} onClick={this.props.onReload} >
                <MUIListItemIcon>
                    <MUIRefreshIcon fontSize="small" />
                </MUIListItemIcon>
                Reload Queue
            </MUIMenuItem>
        );

        actions.push(
            <MUIMenuItem disabled={!this.hasSavedView()} value={"Reset View"} onClick={this.clearSavedView.bind(this)} >
                <MUIListItemIcon>
                    <MUIVisibilityOffIcon fontSize="small" />
                </MUIListItemIcon>
                Reset View
            </MUIMenuItem>
        );

        if (this.props?.showOutOfBoundsToggle !== true) {  // Only show the "View OOB Only" menu item if not on a Dashboard widget
            actions.push(
                <MUIMenuItem value={`View ${this.state.viewOutOfBoundsOnly ? "All" : "OOB Only"}`} onClick={() => { this.setState({ viewOutOfBoundsOnly: !this.state.viewOutOfBoundsOnly, formattedRows: this.getFormattedRows(!this.state.viewOutOfBoundsOnly) }); }} >
                    <MUIListItemIcon>
                        <MUIFilterAltIcon fontSize="small" />
                    </MUIListItemIcon>
                    {`View ${this.state.viewOutOfBoundsOnly ? "All" : "OOB Only"}`}
                </MUIMenuItem>
            );
        }

        actions = actions.concat(this.getActionsInternal());

        actions.push(
            <MUIMenuItem disabled={!this.props.rows || this.props.rows.length === 0} value={"Export CSV"} onClick={this.exportCSV.bind(this)} >
                <MUIListItemIcon>
                    <MUIFileDownloadIcon fontSize="small" />
                </MUIListItemIcon>
                Export CSV
            </MUIMenuItem>
        );

        actions.push(
            <MUIMenuItem disabled={!this.props.rows || this.props.rows.length === 0} value={"Help"} onClick={() => this.setState({openDialogInfo: true})} >
                <MUIListItemIcon>
                    <MUIHelpIcon fontSize="small" />
                </MUIListItemIcon>
                Help
            </MUIMenuItem>
        );

        return actions;
    }

    /**
     * Get any applicable group summary items for the data grid. This is used to
     * add custom values to the grouping rows.
     */
    getGroupSummaryItems() {
        return [];
    }

    /**
     * Override this in subclasses and put any special actions you need in this
     * function.
     */
    getActionsInternal() {
        return [];
    }

    /**
     * Takes an array of raw rows with all available columns and returns an
     * array of rows with only the relevant columns and also formats IDs into
     * strings, etc as the DataGrid component has limited/no knowledge of these
     * objects for performance.
     */
    getFormattedRows(viewOutOfBoundsOnly) {
        const formattedRows = [];

        // Find all of the sourced columns.
        const sourcedColumns = Object.entries(this.columns)
            .filter(([_, value]) => value.source !== undefined)
            .map(([key, value]) => ({
                source: value.source,
                destination: key
            }));

        // Iterate over all of the rows
        this.props.rows.forEach((row) => {
            // Conditionally ignore out of bounds.
            if (viewOutOfBoundsOnly === true && row.out_of_bounds !== true) {
                return;
            }

            // The resultant row object.
            const formattedRow = {};

            // Iterate over all of the columns in the row and format the data.
            Object.keys(row).forEach((key) => {
                if (!this.columns[key]) {
                    console.error(`Column not defined: ${key}`);
                } else {
                    formattedRow[key] = this.getFormattedColumn(
                        row,
                        key,
                        this.columns[key].type
                    );
                }
            });

            // Add in any sourced columns
            sourcedColumns.forEach((column) => {
                formattedRow[column.destination] = this.getFormattedColumn(
                    row,
                    column.source,
                    this.columns[column.destination].type
                );
            });

            formattedRows.push(formattedRow);
        });

        return formattedRows;
    }

    /**
     * @param {object} row The full row.
     * @param {string} key They key for the column to format.
     *
     * @returns A formatted column.
     */
    getFormattedColumn(row, key, type) {
        switch (type) {
            case "user":
                return row[key] === null ? null : User.getDisplayName(this.context.usersIndexed[row[key]]);
            case "orderStatusReason":
                return row[key] === null ? null : OrderStatusReason.getDisplayName(this.context.orderStatusReasonsIndexed[row[key]]);
            case "orderStatusReasonDetail":
                return row[key] === null ? null : OrderStatusReasonDetail.getDisplayName(this.context.orderStatusReasonDetailsIndexed[row[key]]);
            case "orderActivityType":
                return row[key] === null ? null : OrderActivityType.getDisplayName(this.context.orderActivityTypesIndexed[row[key]]);
            case "insuranceType":
                return row[key] === null ? null : InsuranceType.getDisplayName(this.context.insuranceTypesIndexed[row[key]]);
            case "insuranceSubType":
                return row[key] === null ? null : InsuranceSubType.getDisplayName(this.context.insuranceSubTypesIndexed[row[key]]);
            case "productType":
                return row[key] === null ? null : ProductType.getDisplayName(this.context.productTypesIndexed[row[key]]);
            case "productSubType":
                return row[key] === null ? null : ProductSubType.getDisplayName(this.context.productSubTypesIndexed[row[key]]);
            case "productSubTypes":
                if (row[key] === null) {
                    return null;
                } else {
                    const productSubTypes = JSON.parse(row[key]);
                    return "[" + productSubTypes.length + "] " + productSubTypes
                        .map(productSubType => ProductSubType.getDisplayName(this.context.productSubTypesIndexed[productSubType]))
                        .join(" • ");
                }
            case "location":
                return row[key] === null ? null : Location.getDisplayName(this.context.locationsIndexed[row[key]]);
            case "region":
                return row[key] === null ? null : Region.getDisplayName(this.context.regionsIndexed[row[key]]);
            case "orderStatus":
                return row[key] === null ? null : OrderStatus.getDisplayName(this.context.orderStatusesIndexed[row[key]]);
            case "datetime":
                return row[key] === null ? null : moment.utc(row[key]).format("YYYY-MM-DD HH:mm:ss");
            case "date":
            case "datePicker":
                return row[key] === null ? null : moment.utc(row[key]).format("YYYY-MM-DD");
            case "dateMonthYear":
                return row[key] === null ? null : moment.utc(row[key]).format("YYYY-MM");
            case "issueCategory":
                return row[key] === null ? null : IssueCategory.getDisplayName(this.context.issueCategoriesIndexed[row[key]]);
            case "issueReason":
                return row[key] === null ? null : IssueReason.getDisplayName(this.context.issueReasonsIndexed[row[key]]);
            case "patientIssueIssueStatus":
                return row[key] === null ? null : row[key] === 1 ? 'Closed' : 'Open';
            case "term":
                return row[key] === null ? null : Term.getDisplayName(this.context.termsIndexed[row[key]]);
            case "marketingCampaign":
                return row[key] === null ? null : MarketingCampaign.getDisplayName(this.context.marketingCampaignsIndexed[row[key]]);
            case "marketingCampaignCategory":
                return row[key] === null ? null : MarketingCampaignCategory.getDisplayName(this.context.marketingCampaignCategoriesIndexed[row[key]]);
            case "boolean":
                return row[key] === null ? null : row[key] === true ? "Yes" : "No";
            case "phone":
                return row[key] === null ? null : row[key].replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3').padStart(14, row[key]);
            case "brightreeInvoicePayorName":
                return row[key] === null ? null : row[key].toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
            case "brightreeInvoicePatientName":
                return row[key] === null ? null : row[key].replace(/^([\w\s-]+), ([\w\s-]+)$/, (_, last, first) => `${first} ${last}`).toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
            case "errorDate":
                if (Queue.reasonIsInQueueType(Object.values(this.context.queuesIndexed), row["order_activity.order_status_reason_id"], 2)) {
                    return row[key] === null ? null : moment.utc(row[key]).format("YYYY-MM-DD");
                } else {
                    return null;
                }
            case "servicePartCatalog":
                return row[key] === null ? null : ServicePartCatalog.getDisplayName(this.context.servicePartCatalogsIndexed[row[key]]);
            // This logic is specific to CardQueueDataGridServiceQuotePart.
            case "serviceQuotePart.partName":
                if (row["service_quote_part.product.product_sub_type_id"]) {
                    // TODO Fix this to look it up
                    return row["service_quote_part.product.product_sub_type_id"];
                } else if (row["service_quote_part.service_part_catalog_id"]) {
                    return ServicePartCatalog.getDisplayName(this.context.servicePartCatalogsIndexed[row[key]]);
                } else {
                    return null;
                }
            case "vendor":
                return row[key] === null ? null : Vendor.getDisplayName(this.context.vendorsIndexed[row[key]]);
            case "distributionReason":
                return row[key] === null ? null : DistributionReason.getDisplayName(this.context.distributionReasonsIndexed[row[key]]);
            case "distributionReasonDescriptions":
                if (row[key] === null) {
                    return null;
                } else {
                    const distributionReasonByCode = Object.values(this.context.distributionReasonsIndexed).reduce((accumulator, distributionReason) => {
                        accumulator[distributionReason.code] = distributionReason;
                        return accumulator;
                    }, {});

                    return row[key]
                        .split(/[,\n;]+/)
                        .map((distributionReasonCode) => distributionReasonCode.trim())
                        .filter((distributionReasonCode) => distributionReasonCode.length > 0)
                        .map((distributionReasonCode) => {
                            const distributionReason = distributionReasonByCode[distributionReasonCode];
                            return distributionReason ? DistributionReason.getDisplayName(distributionReason) : distributionReasonCode;
                        })
                        .join("\n");
                }
            case "inboundCommunicationBody":
                if (row[key] === null) {
                    return null;
                } else {
                    const jsonData = JSON.parse(row[key]);
                    return jsonData.Body
                }
            case "inboundCommunicationMediaUrls":
                if (row[key] === null) {
                    return null;
                } else {
                    const jsonData = JSON.parse(row[key]);
                    const numMedia = jsonData.NumMedia;
                    let mediaUrls = [];
                    if (numMedia > 0) {
                        for (let i = 0; i < numMedia; i++) {
                            mediaUrls.push(
                                <MUIButton
                                    key={`MediaUrl${i}`}
                                    sx={{
                                        margin: 0,
                                        padding: 0,
                                        minWidth: 30,
                                        '&:hover': {
                                            backgroundColor: "transparent"
                                        }
                                    }}
                                    disableRipple
                                    href={jsonData[`MediaUrl${i}`]}
                                    target="_blank"
                                    startIcon={<MUIOpenInNewIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                </MUIButton>);
                        }
                    }
                    return mediaUrls;
                }
            case "inboundCommunicationFromPhoneNumber":
                if (row[key] === null) {
                    return null;
                } else {
                    const jsonData = JSON.parse(row[key]);
                    const phoneNumber = jsonData.From?.toString().replace('+1', "").replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3').padStart(14, row[key]);
                    return phoneNumber;
                }
            case "serviceDocumentationFollowupReceivedNotification":
            case "servicePreauthFollowupReceivedNotification":
                {
                    const notificationConfigByType = {
                        "serviceDocumentationFollowupReceivedNotification": {
                            notificationTypeTermIds: [92, 93],
                            businessDays: 3,
                        },
                        "servicePreauthFollowupReceivedNotification": {
                            notificationTypeTermIds: [94, 95],
                            businessDays: 3,
                        },
                    };

                    const config = notificationConfigByType[type];
                    const notificationTypeTermId = Number(row["order_activity.last_successful_notification.notification_type_term_id"]);
                    const statusChangedAt = row["order_activity.last_successful_notification.last_notification_status_term_id_change"];

                    if (!statusChangedAt || !config.notificationTypeTermIds.includes(notificationTypeTermId)) {
                        return false;
                    }

                    const cutoff = moment(Calendar.addDays({
                        daysToAdd: -config.businessDays,
                        useBusinessDays: true
                    })).startOf("day");
                    const statusChangedAtMoment = moment.utc(statusChangedAt).local();
                    if (statusChangedAtMoment.isValid() === false) {
                        return false;
                    }

                    return statusChangedAtMoment.isSameOrAfter(cutoff);
                }
            default:
                return row[key];
        }
    }

    /**
     * Saves the sorting and updates the state so the grid rerenders.
     *
     * @param {Array} sorting
     */
    handleSortingChange(sorting) {
        this.setState({
            sorting: sorting
        });
        Setting.set(`queue.${this.props.queue.id}.sorting`, sorting);
    }

    /**
     * Saves the grouping and updates the state so the grid rerenders.
     *
     * @param {Array} grouping
     */
    handleGroupingChange(grouping) {
        // Remove nonDefaultSorting values from updatedSorting
        const finalSorting =  this.updateSortingFromGrouping(grouping, this.state.sorting, this.props.queue.view.sorts || []);

        this.applyGroupingToSorting(grouping, finalSorting);
    }

    /**
     * Saves the expanded groups and updates the state so the grid rerenders.
     *
     * @param {Array} expandedGroups
     */
    handleExpandedGroupsChange(expandedGroups) {
        this.setState({
            expandedGroups: expandedGroups
        });
        Setting.set(`queue.${this.props.queue.id}.expandedGroups`, expandedGroups);
    }

    /**
    * Updates sorting to include all grouping columns in ascending order
    * and ensures that sorting reflects the current grouping and default settings.
    *
    * @param {Array} grouping - The updated grouping list.
    * @param {Array} currentSorting - The current sorting list.
    * @param {Array} defaultSorting - The default sorting list.
    * @returns {Array} - The updated sorting list.
    */
    updateSortingFromGrouping(grouping, currentSorting, defaultSorting) {
        // Extract column names from the new grouping
        const newGroupingColumns = new Set(grouping.map(g => g.columnName));

        // Create a set of column names currently in the sorting list
        const currentSortingColumns = new Set(currentSorting.map(s => s.columnName));

        // Add new grouping columns to the sorting list
        const updatedSorting = [...currentSorting];
        newGroupingColumns.forEach(columnName => {
            if (!currentSortingColumns.has(columnName)) {
                updatedSorting.push({
                    columnName: columnName,
                    direction: 'asc'  // Default sorting direction
                });
            }
        });

        // Remove columns from the sorting list that are no longer in grouping
        const filteredSorting = updatedSorting.filter(sort => !newGroupingColumns.has(sort.columnName));

        // Extract column names from defaultSorting
        const defaultSortingColumns = new Set(defaultSorting.map(sort => sort['key']));

        // Create a new array of values in filteredSorting that are not in defaultSorting
        const nonDefaultSorting = filteredSorting.filter(sort => !defaultSortingColumns.has(sort.columnName));

        // Remove nonDefaultSorting values from updatedSorting
        return updatedSorting.filter(sort => !nonDefaultSorting.some(nonDefault => nonDefault.columnName === sort.columnName));
    }

    applyGroupingToSorting(grouping, sorting) {
        // Update state
        this.setState({
            grouping: grouping,
            sorting: sorting
        });

        // Save new settings
        Setting.set(`queue.${this.props.queue.id}.grouping`, grouping);
        Setting.set(`queue.${this.props.queue.id}.sorting`, sorting);
    }

    /**
     * Handle clicking on a grid row. Probably want to override this in the
     * child class.
     */
    handleClickRow(row) {
        console.log(row);
    }

    /**
     * @param {string} rowAction The action that was performed.
     * @param {object} row The row the action was performed for.
     * @param {object} data Optional extra data
     */
    handleClickRowAction(rowAction, row, data) {
        switch (rowAction) {
            case "syncToBrightree":
                // Defined in CardQueueDataGrid
                this.handleSyncToBrightree(row);
                break;
            case "updateOrderActivity":
                // Defined in CardQueueDataGridOrderActivity
                this.handleUpdateOrderActivity(row, data.orderActivity);
                break;
            case "openDatePicker":
                // Defined in CardQueueDataGridOrderActivitySales
                this.handleOpenDatePicker(row, data.columnName, data.value);
                break;
            case "atpOpenDatePicker":
                // Defined in CardQueueDataGridOrderActivitySales
                this.handleOpenAtpDatePicker(row, data.columnName, data.value);
                break;
            case "openEvaluationTypeAndDatePicker":
                // Defined in CardQueueDataGridOrderActivitySales
                this.handleOpenEvaluationTypeAndDatePicker(row, data.columnName, data.value);
                break;
            case "openIntakeQuestionnaire":
                // Defined in CardQueueDataGridOrderActivitySales
                this.handleOpenIntakeQuestionnaireDialog(row);
                break;
            default:
                console.warn(`Row action not defined: ${rowAction}`);
                break;
        }
    }

    /**
     * @param {object} row The row to sync.
     */
    handleSyncToBrightree(row) {
        const syncingSnackbar = NotistackEnqueueSnackbar('Syncing to Brightree...', { variant: "info", persist: true });
        orderService.syncOrderWithBT(row["order_activity.id"])
            .then(() => {
                NotistackCloseSnackbar(syncingSnackbar);
                NotistackEnqueueSnackbar('Sync complete!', { variant: "success" });
                this.props.onReload();
            })
            .catch(() => {
                NotistackCloseSnackbar(syncingSnackbar);
                NotistackEnqueueSnackbar('Sync failed!', { variant: "error" });
            });
    }

    /**
     * Clear saved view.
     */
    clearSavedView() {
        Setting.clear(`queue.${this.props.queue.id}.sorting`);
        Setting.clear(`queue.${this.props.queue.id}.grouping`);
        Setting.clear(`queue.${this.props.queue.id}.expandedGroups`);
        Setting.clear(`queue.${this.props.queue.id}.searchQuery`)

        // Get default sorts and groups from the queue view
        const defaultSorts = this.props.queue.view.sorts || [];
        const defaultGroups = this.props.queue.view.groups || [];

        // Create default sorting array from default sorts
        const defaultSorting = defaultSorts.map(sort => ({
            columnName: sort.key,
            direction: sort.direction,
        }));

        // Create default grouping array
        const defaultGrouping = defaultGroups.map(group => ({
            columnName: group,
        }));

        // Ensure all grouping columns are in the sorting list with 'asc' direction
        const sortingColumns = new Set(defaultSorting.map(sort => sort.columnName));

        const updatedSorting = [
            ...defaultSorting,
            ...defaultGrouping.filter(group => !sortingColumns.has(group.columnName)).map(group => ({
                columnName: group.columnName,
                direction: 'asc',  // Default sorting direction
            }))
        ];

        this.applyGroupingToSorting(defaultGrouping, updatedSorting);

        // Update state
        this.setState({
            expandedGroups: [],
        });

        this.props.onReload()
    }

    /**
     * @return {boolean} Whether or not there is currently a saved view.
     */
    hasSavedView() {
        return (
            Setting.get(`queue.${this.props.queue.id}.sorting`) !== undefined ||
            Setting.get(`queue.${this.props.queue.id}.grouping`) !== undefined ||
            Setting.get(`queue.${this.props.queue.id}.expandedGroups`) !== undefined
        );
    }

    exportCSV() {
        const csvRows = [];

        // Header row
        const columns = [];
        this.props.queue.view.columns.forEach((column) => {
            if (this.columns[column].excludeFromCsv) {
                return;
            }
            columns.push(this.columns[column].name);
        });
        csvRows.push(columns);

        // Table rows
        const rows = this.state.formattedRows;
        rows.forEach((row) => {
            const csvRow = [];
            this.props.queue.view.columns.forEach((column) => {
                if (this.columns[column].excludeFromCsv) {
                    return;
                }
                csvRow.push(row[column]);
            });
            csvRows.push(csvRow);
        });

        const filename = `${this.props.queue.name} - ${moment(this.props.loadedAt).format("YYYY-MM-DD h mm a")}.csv`;

        Exporter.exportCSV(csvRows, filename);
    }
}
